<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconFilm from '@/components/icon/IconFilm.vue'
import IconGlobe from '@/components/icon/IconGlobe.vue'
import IconImage from '@/components/icon/IconImage.vue'
import MediaflowLiveLayer from './MediaflowLiveLayer.vue'
import {
	layerBox, boxToTransform, clampBox, clampMove, clampResize, containRect, snapLines, renderOrder, zIndexOf, urlViewportFit, vodSourceUrl, URL_VIEWPORT,
} from './sceneGeometry'

/**
 * Browser-drawn preview of one flow, using the same geometry as the server
 * renderer (see sceneGeometry.js). Layers are positioned in canvas pixels
 * scaled to the container, so the picture matches what airs.
 */
const props = defineProps({
	flow: { type: Object, default: null },
	canvas: { type: Object, default: () => ({ width: 1920, height: 1080 }) },
	/** (media) => transform — lets the parent inject draft transforms. */
	transformFor: { type: Function, default: (media) => media?.transform || null },
	selectedId: { type: String, default: null },
	/** Layers can be selected / dragged / resized. */
	editable: { type: Boolean, default: true },
	onAir: { type: Boolean, default: false },
	liveEditing: { type: Boolean, default: false },
	audioMonitor: { type: Boolean, default: false },
	lightweight: { type: Boolean, default: false },
	/** All streams, to resolve a live media's stream object. */
	streams: { type: Array, default: () => [] },
	/** Plain view (e.g. mobile): no selection, no handles. */
	interactive: { type: Boolean, default: true },
	/** Snap to canvas edges/centre and other layers while dragging. */
	snap: { type: Boolean, default: true },
	/** Production still loading: show a skeleton instead of an empty stage. */
	loading: { type: Boolean, default: false },
	/**
	 * Production-level graphics drawn above the flow with the same geometry:
	 * every live overlay, plus the one being edited (ghosted when off air).
	 * `overlays[0]` is the top-most layer.
	 */
	overlays: { type: Array, default: () => [] },
	/** Accept inputs dragged from the Inputs row (emits `drop-input`). */
	dropTarget: { type: Boolean, default: false },
	/** Shown when there is no flow. */
	placeholder: { type: String, default: 'Pick a scene to preview it here.' },
})

const emit = defineEmits(['select', 'transform', 'fullscreen-change', 'drop-input', 'gesture-box'])

const stage = ref(null)
const stageWidth = ref(0)
const isFullscreen = ref(false)
const scale = computed(() => (props.canvas.width ? stageWidth.value / props.canvas.width : 0))
const aspectRatio = computed(() => `${props.canvas.width} / ${props.canvas.height}`)
// In fullscreen the stage keeps its aspect ratio centred on a black viewport.
const stageStyle = computed(() => (isFullscreen.value
	? { width: `min(100vw, calc(100vh * ${props.canvas.width / props.canvas.height}))`, aspectRatio: aspectRatio.value }
	: null))

let resizeObserver = null
onMounted(() => {
	if (typeof ResizeObserver !== 'undefined' && stage.value) {
		resizeObserver = new ResizeObserver((entries) => {
			const entry = entries[0]
			if (entry) stageWidth.value = entry.contentRect.width
		})
		resizeObserver.observe(stage.value)
	}
	stageWidth.value = stage.value?.clientWidth || 0
	document.addEventListener('fullscreenchange', onFullscreenChange)
})
onBeforeUnmount(() => {
	resizeObserver?.disconnect()
	document.removeEventListener('fullscreenchange', onFullscreenChange)
	cancelGesture()
	flushNudge()
})

// ---------------------------------------------------------------- layers
const media = computed(() => (Array.isArray(props.flow?.media) ? props.flow.media : []))
const canEdit = computed(() => props.editable && props.interactive)

// The renderer contain-fits content inside the stored box. Each layer is drawn
// (and selected, hit-tested, dragged) at that visible rect, so there is never
// an invisible frame to grab. `null` aspect = the content fills its box.
const aspects = reactive({})
const setAspect = (id, aspect) => {
	const a = Number.isFinite(aspect) && aspect > 0 ? aspect : null
	if (aspects[id] !== a) aspects[id] = a
}
const onImageLoad = (event, id) => setAspect(id, event.target.naturalWidth / event.target.naturalHeight)
const onVideoMeta = (event, id) => setAspect(id, event.target.videoWidth / event.target.videoHeight)
const URL_ASPECT = URL_VIEWPORT.width / URL_VIEWPORT.height
const contentAspect = (item) => {
	if (item.type === 'image') return item.imageUrl ? aspects[item.id] ?? null : null
	if (props.lightweight) return null
	if (item.type === 'vod') return vodSourceUrl(item) ? aspects[item.id] ?? null : null
	if (item.type === 'live') return aspects[item.id] ?? null
	if (item.type === 'url') return item.sourceUrl ? URL_ASPECT : null
	return null
}
const toLayer = (item, box, extra) => {
	const aspect = contentAspect(item)
	return { media: item, box, aspect, rect: containRect(box, aspect), ...extra }
}

const flowLayers = computed(() => {
	const list = media.value
	const ordered = renderOrder(list)
	return ordered.map((item) => {
		const index = list.indexOf(item)
		return toLayer(item, layerBox(props.transformFor(item), props.canvas), { index, visible: item.show !== false, z: zIndexOf(list, index), overlay: null })
	})
})
// Graphics sit above every flow layer; `overlays[0]` is the top-most.
const OVERLAY_Z = 500
const overlayLayers = computed(() => {
	const list = (Array.isArray(props.overlays) ? props.overlays : []).filter((o) => o && o.media)
	return list.map((overlay, i) => toLayer(overlay.media, layerBox(props.transformFor(overlay.media), props.canvas), {
		index: i,
		// An off-air graphic is only drawn while it is being edited (ghosted).
		visible: !!overlay.live,
		z: OVERLAY_Z + (list.length - i),
		overlay,
	}))
})
const layers = computed(() => [...flowLayers.value, ...overlayLayers.value])
// Hidden layers are drawn ghosted only while selected, so they can still be placed.
const drawnLayers = computed(() => layers.value.filter((l) => l.visible || l.media.id === props.selectedId))
const selectedLayer = computed(() => layers.value.find((l) => l.media.id === props.selectedId) || null)
const flowDrawn = computed(() => drawnLayers.value.some((l) => !l.overlay))

// ---------------------------------------------------------------- input drop
const dropActive = ref(false)
const INPUT_DRAG_TYPE = 'application/x-castr-input'
const hasInputDrag = (event) => Array.from(event.dataTransfer?.types || []).includes(INPUT_DRAG_TYPE)
const onDragOver = (event) => {
	if (!props.dropTarget || !hasInputDrag(event)) return
	event.preventDefault()
	event.dataTransfer.dropEffect = 'copy'
	dropActive.value = true
}
const onDragLeave = (event) => {
	if (event.currentTarget.contains(event.relatedTarget)) return
	dropActive.value = false
}
const onDrop = (event) => {
	dropActive.value = false
	if (!props.dropTarget || !hasInputDrag(event)) return
	event.preventDefault()
	let payload = null
	try { payload = JSON.parse(event.dataTransfer.getData(INPUT_DRAG_TYPE) || 'null') } catch (e) { payload = null }
	if (!payload) return
	// Where it was dropped, in canvas pixels, so the parent can place it there.
	const rect = stage.value?.getBoundingClientRect()
	const point = rect && scale.value
		? { x: (event.clientX - rect.left) / scale.value, y: (event.clientY - rect.top) / scale.value }
		: null
	emit('drop-input', payload, point)
}

const px = (n) => `${Math.round(n * scale.value * 100) / 100}px`
const rectStyle = (r) => ({ left: px(r.x), top: px(r.y), width: px(r.w), height: px(r.h) })
const boxStyle = (box, z) => ({ ...rectStyle(box), zIndex: z })

const streamFor = (item) => {
	const id = typeof item.stream === 'object' && item.stream !== null ? (item.stream._id || item.stream.id) : item.stream
	return props.streams.find((s) => s._id === id) || null
}
const volumeOf = (item) => Math.max(0, Math.min(1, (Number(item.volume) ?? 100) / 100))
const mutedFor = (item) => !props.audioMonitor || !!item.muted
const urlFrameStyle = (box) => {
	const fit = urlViewportFit({ w: box.w * scale.value, h: box.h * scale.value })
	return {
		width: `${URL_VIEWPORT.width}px`,
		height: `${URL_VIEWPORT.height}px`,
		transform: `translate(${fit.offsetX}px, ${fit.offsetY}px) scale(${fit.scale})`,
		transformOrigin: '0 0',
	}
}

// ---------------------------------------------------------------- editing
// A drag never writes reactive state: once per frame the layer gets a GPU
// transform and the selection frame new coordinates, straight on the DOM.
// The result is emitted once, on pointer-up. Everything works on the visible
// content rect; the committed box hugs it.
const selectionEl = ref(null)
const guideX = ref(null)
const guideY = ref(null)
let gesture = null
let frame = 0
let suppressClick = false

const otherRects = (excludeId) => layers.value.filter((l) => l.visible && l.media.id !== excludeId).map((l) => l.rect)
const layerEl = (id) => Array.from(stage.value?.querySelectorAll('[data-layer-id]') || []).find((el) => el.dataset.layerId === id) || null

const onStageClick = (event) => {
	if (!canEdit.value) return
	if (suppressClick) { suppressClick = false; return }
	if (event.target === stage.value || event.target?.dataset?.stageBg !== undefined) emit('select', null)
}

// Handles sit on the box edges, or just inside them where the box is flush
// with the canvas edge, so they are never clipped; same for the name label.
const EDGE_PX = 6
const LABEL_PX = 24
// The name label sits above the selection, else below it, else inside; it
// hangs from the side nearest the canvas centre so it never runs off the canvas.
const selectionGeometry = (box) => {
	const s = scale.value
	const roomAbove = box.y * s
	const roomBelow = (props.canvas.height - box.y - box.h) * s
	const below = roomAbove < LABEL_PX && roomBelow >= LABEL_PX
	const inside = roomAbove < LABEL_PX && !below
	const right = box.x + box.w / 2 > props.canvas.width / 2
	const room = (right ? box.x + box.w : props.canvas.width - box.x) * s
	return {
		style: { ...rectStyle(box), '--label-max': `${Math.max(48, Math.round(room - (inside ? 36 : 4)))}px` },
		flush: {
			l: box.x * s < EDGE_PX,
			r: (props.canvas.width - box.x - box.w) * s < EDGE_PX,
			t: box.y * s < EDGE_PX,
			b: (props.canvas.height - box.y - box.h) * s < EDGE_PX,
			label: inside,
			labelBelow: below,
			labelRight: right,
		},
	}
}
const selectionAttrs = (box) => {
	const { style, flush } = selectionGeometry(box)
	return {
		style,
		'data-flush-l': flush.l ? '' : undefined,
		'data-flush-r': flush.r ? '' : undefined,
		'data-flush-t': flush.t ? '' : undefined,
		'data-flush-b': flush.b ? '' : undefined,
		'data-label-inside': flush.label ? '' : undefined,
		'data-label-below': flush.labelBelow ? '' : undefined,
		'data-label-right': flush.labelRight ? '' : undefined,
	}
}
/** Paint the selection frame for `box`; while moving, `from` is the box it is laid out at (GPU translate only). */
const placeSelection = (box, from = null) => {
	const el = selectionEl.value
	if (!el || !box) return
	const { style, flush } = selectionGeometry(box)
	if (from) {
		const s = scale.value
		el.style.transform = `translate3d(${(box.x - from.x) * s}px, ${(box.y - from.y) * s}px, 0)`
	} else {
		el.style.transform = ''
		const { '--label-max': _labelMax, ...rect } = style
		Object.assign(el.style, rect)
	}
	el.style.setProperty('--label-max', style['--label-max'])
	el.toggleAttribute('data-flush-l', flush.l)
	el.toggleAttribute('data-flush-r', flush.r)
	el.toggleAttribute('data-flush-t', flush.t)
	el.toggleAttribute('data-flush-b', flush.b)
	el.toggleAttribute('data-label-inside', flush.label)
	el.toggleAttribute('data-label-below', flush.labelBelow)
	el.toggleAttribute('data-label-right', flush.labelRight)
}

const beginGesture = (event, layer, mode, handle = null) => {
	cancelGesture()
	flushNudge()
	const el = layerEl(layer.media.id)
	gesture = {
		mode,
		handle,
		media: layer.media,
		aspect: layer.aspect,
		start: { ...layer.rect },
		box: { ...layer.rect },
		rect: { ...layer.rect },
		px: event.clientX,
		py: event.clientY,
		x: event.clientX,
		y: event.clientY,
		shift: event.shiftKey,
		alt: event.altKey,
		moved: false,
		layout: false,
		el,
		pointerId: event.pointerId,
		lines: props.snap ? snapLines(props.canvas, otherRects(layer.media.id)) : null,
		snap: { x: { cur: null }, y: { cur: null } },
	}
	// With the pointer captured, the click that follows lands on the stage: it must not deselect.
	suppressClick = true
	try { stage.value?.setPointerCapture?.(event.pointerId) } catch (e) { /* synthetic pointer */ }
	if (el) el.style.willChange = 'transform'
	window.addEventListener('pointermove', onPointerMove)
	window.addEventListener('pointerup', onPointerUp)
	window.addEventListener('pointercancel', onPointerUp)
}

const onLayerPointerDown = (event, layer) => {
	if (!canEdit.value || event.button !== 0) return
	event.preventDefault()
	event.stopPropagation()
	if (props.selectedId !== layer.media.id) emit('select', layer.media.id)
	beginGesture(event, layer, 'move')
	stage.value?.focus({ preventScroll: true })
}

const onHandlePointerDown = (event, handle) => {
	const layer = selectedLayer.value
	if (!layer || !canEdit.value || event.button !== 0) return
	event.preventDefault()
	event.stopPropagation()
	beginGesture(event, layer, 'resize', handle)
}

/** Opposite edge/corner stays put; with `keepAspect` the box keeps `start`'s ratio. */
const resizeBox = (start, handle, dx, dy, keepAspect) => {
	let { x, y, w, h } = start
	const hasW = handle.includes('w')
	const hasE = handle.includes('e')
	const hasN = handle.includes('n')
	const hasS = handle.includes('s')
	if (hasE) w = start.w + dx
	if (hasW) { w = start.w - dx; x = start.x + dx }
	if (hasS) h = start.h + dy
	if (hasN) { h = start.h - dy; y = start.y + dy }
	const isCorner = (hasW || hasE) && (hasN || hasS)
	if (keepAspect && isCorner) {
		const aspect = start.w / start.h
		// Follow the dominant axis so the corner tracks the pointer.
		if (Math.abs(dx) >= Math.abs(dy) * aspect) h = w / aspect
		else w = h * aspect
		if (hasW) x = start.x + start.w - w
		if (hasN) y = start.y + start.h - h
	}
	return { x, y, w, h }
}

// Snapping: catches within SNAP_IN screen px and lets go only past SNAP_OUT,
// so a box neither jumps between nearby lines nor sticks to one.
const SNAP_IN = 6
const SNAP_OUT = 10
const snapAxis = (state, values, lines) => {
	const s = scale.value
	if (state.cur && Math.abs(values[state.cur.i] - state.cur.line) <= SNAP_OUT / s) return state.cur
	let best = null
	values.forEach((v, i) => lines.forEach((line) => {
		const d = Math.abs(line - v)
		if (d <= SNAP_IN / s && (!best || d < best.d)) best = { i, line, d }
	}))
	state.cur = best ? { i: best.i, line: best.line } : null
	return state.cur
}
const snapMove = (box, g) => {
	const out = { ...box }
	const guides = []
	const offX = [0, box.w / 2, box.w]
	const offY = [0, box.h / 2, box.h]
	const hx = snapAxis(g.snap.x, offX.map((o) => box.x + o), g.lines.xs)
	if (hx) { out.x = hx.line - offX[hx.i]; guides.push({ axis: 'x', pos: hx.line }) }
	const hy = snapAxis(g.snap.y, offY.map((o) => box.y + o), g.lines.ys)
	if (hy) { out.y = hy.line - offY[hy.i]; guides.push({ axis: 'y', pos: hy.line }) }
	return { box: out, guides }
}
const snapResize = (box, g, keepAspect) => {
	const hasW = g.handle.includes('w')
	const hasE = g.handle.includes('e')
	const hasN = g.handle.includes('n')
	const hasS = g.handle.includes('s')
	const right = box.x + box.w
	const bottom = box.y + box.h
	const out = { ...box }
	const guides = []
	const hx = (hasW || hasE) ? snapAxis(g.snap.x, [hasW ? box.x : right], g.lines.xs) : null
	const hy = (hasN || hasS) && !(keepAspect && hx) ? snapAxis(g.snap.y, [hasN ? box.y : bottom], g.lines.ys) : null
	if (!hy && keepAspect) g.snap.y.cur = null
	const aspect = g.start.w / g.start.h
	if (hx) {
		out.w = hasW ? right - hx.line : hx.line - box.x
		if (hasW) out.x = hx.line
		guides.push({ axis: 'x', pos: hx.line })
		if (keepAspect && (hasN || hasS)) { out.h = out.w / aspect; if (hasN) out.y = bottom - out.h }
	} else if (hy) {
		out.h = hasN ? bottom - hy.line : hy.line - box.y
		if (hasN) out.y = hy.line
		guides.push({ axis: 'y', pos: hy.line })
		if (keepAspect && (hasW || hasE)) { out.w = out.h * aspect; if (hasW) out.x = right - out.w }
	}
	return { box: out, guides }
}

const onPointerMove = (event) => {
	if (!gesture) return
	gesture.x = event.clientX
	gesture.y = event.clientY
	gesture.shift = event.shiftKey
	gesture.alt = event.altKey
	if (!frame) frame = requestAnimationFrame(applyFrame)
}

/** One frame of a gesture: compute the box (snap → clamp) and paint it. */
const applyFrame = () => {
	frame = 0
	const g = gesture
	if (!g || !scale.value) return
	const s = scale.value
	const dx = (g.x - g.px) / s
	const dy = (g.y - g.py) / s
	if (!g.moved && Math.abs(dx) < 1 && Math.abs(dy) < 1) return
	g.moved = true
	const S = g.start
	let guides = []
	if (g.mode === 'move') {
		let raw = { x: S.x + dx, y: S.y + dy, w: S.w, h: S.h }
		if (g.lines) ({ box: raw, guides } = snapMove(raw, g))
		const box = g.alt ? raw : clampMove(raw, props.canvas)
		g.box = box
		g.rect = box
		if (g.el) g.el.style.transform = `translate3d(${(box.x - S.x) * s}px, ${(box.y - S.y) * s}px, 0)`
		placeSelection(box, S)
	} else {
		const isCorner = g.handle.length === 2
		// Corners keep the shape unless Shift is held; edges (only offered when the
		// content has no intrinsic shape) are free.
		const keepAspect = isCorner && !g.shift
		let raw = resizeBox(S, g.handle, dx, dy, keepAspect)
		if (g.lines) ({ box: raw, guides } = snapResize(raw, g, keepAspect))
		const box = g.alt ? clampBox(raw, props.canvas) : clampResize(raw, S, g.handle, props.canvas, keepAspect)
		g.box = box
		g.rect = g.aspect ? containRect(box, g.aspect) : box
		if (g.el) {
			if (keepAspect) {
				// Same shape: a uniform GPU scale of the laid-out layer, no distortion.
				g.el.style.transform = `translate3d(${(box.x - S.x) * s}px, ${(box.y - S.y) * s}px, 0) scale(${box.w / S.w})`
			} else {
				// Free: lay the content out at its fitted rect so it never stretches.
				g.layout = true
				g.el.style.transform = ''
				Object.assign(g.el.style, rectStyle(g.rect))
			}
		}
		placeSelection(box)
	}
	showGuides(guides)
	emit('gesture-box', { mediaId: g.media.id, box: g.rect })
}

/** Snap guides: one vertical + one horizontal line, moved on the GPU. */
const showGuides = (lines = []) => {
	const s = scale.value
	const x = lines.find((l) => l.axis === 'x')
	const y = lines.find((l) => l.axis === 'y')
	if (guideX.value) {
		guideX.value.hidden = !x
		if (x) guideX.value.style.transform = `translate3d(${x.pos * s}px, 0, 0)`
	}
	if (guideY.value) {
		guideY.value.hidden = !y
		if (y) guideY.value.style.transform = `translate3d(0, ${y.pos * s}px, 0)`
	}
}

const endGesture = (commit) => {
	const g = gesture
	gesture = null
	window.removeEventListener('pointermove', onPointerMove)
	window.removeEventListener('pointerup', onPointerUp)
	window.removeEventListener('pointercancel', onPointerUp)
	if (frame) { cancelAnimationFrame(frame); frame = 0 }
	if (!g) return
	try { stage.value?.releasePointerCapture?.(g.pointerId) } catch (e) { /* already released */ }
	showGuides()
	setTimeout(() => { suppressClick = false }, 0)
	const committed = commit && g.moved
	if (committed) emit('transform', g.media, boxToTransform(g.box, props.canvas), { final: true })
	if (g.moved) emit('gesture-box', null)
	// The committed transform re-renders the layer before the next paint.
	if (g.el) {
		g.el.style.transform = ''
		g.el.style.willChange = ''
		if (g.layout && !committed) Object.assign(g.el.style, rectStyle(g.start))
	}
	nextTick(() => {
		const layer = layers.value.find((l) => l.media.id === g.media.id)
		if (g.layout && g.el && layer) Object.assign(g.el.style, rectStyle(layer.rect))
		placeSelection(selectedLayer.value?.rect)
	})
}
const cancelGesture = () => endGesture(false)

const onPointerUp = (event) => {
	if (!gesture) return
	if (event.type === 'pointercancel') { cancelGesture(); return }
	gesture.x = event.clientX
	gesture.y = event.clientY
	applyFrame()
	endGesture(true)
}

// Arrow nudges move at once; one save (and one undo step) per burst.
const NUDGE_SAVE_MS = 400
let nudge = null
const flushNudge = () => {
	if (!nudge) return
	const { media: item, transform } = nudge
	clearTimeout(nudge.timer)
	nudge = null
	emit('transform', item, transform, { final: true })
}
const onKeydown = (event) => {
	if (!canEdit.value || !selectedLayer.value) return
	const step = event.shiftKey ? 10 : 1
	const delta = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[event.key]
	if (event.key === 'Escape') { flushNudge(); emit('select', null); return }
	if (!delta) return
	event.preventDefault()
	const layer = selectedLayer.value
	if (nudge && nudge.media.id !== layer.media.id) flushNudge()
	const r = layer.rect
	const raw = { ...r, x: r.x + delta[0], y: r.y + delta[1] }
	const transform = boxToTransform(event.altKey ? raw : clampMove(raw, props.canvas), props.canvas)
	clearTimeout(nudge?.timer)
	nudge = { media: layer.media, transform, timer: setTimeout(flushNudge, NUDGE_SAVE_MS) }
	emit('transform', layer.media, transform, { final: false })
}
watch(() => props.selectedId, () => flushNudge())

// ---------------------------------------------------------------- fullscreen
const onFullscreenChange = () => {
	isFullscreen.value = !!document.fullscreenElement && stage.value?.parentElement?.contains(document.fullscreenElement)
	emit('fullscreen-change', isFullscreen.value)
}
const toggleFullscreen = async () => {
	const el = stage.value?.parentElement
	if (!el) return
	try {
		if (document.fullscreenElement) await document.exitFullscreen()
		else await el.requestFullscreen()
	} catch (err) {
		console.error('fullscreen failed', err)
	}
}

watch(() => props.flow?._id, () => cancelGesture())

const HANDLES = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w']
const CORNERS = ['nw', 'ne', 'se', 'sw']
const handleCursor = (handle) => ({ n: 'ns-resize', s: 'ns-resize', e: 'ew-resize', w: 'ew-resize', ne: 'nesw-resize', sw: 'nesw-resize', nw: 'nwse-resize', se: 'nwse-resize' }[handle])

/** Visible rect + intrinsic aspect of a layer (for the Position numbers). */
const layerGeometry = (id) => {
	const layer = layers.value.find((l) => l.media.id === id)
	return layer ? { rect: { ...layer.rect }, aspect: layer.aspect } : null
}

defineExpose({ toggleFullscreen, isFullscreen, layerGeometry })
</script>

<template>
	<div
		class="scene-canvas relative isolate w-full select-none overflow-hidden bg-black ring-1"
		:class="liveEditing ? 'ring-2 ring-error' : 'ring-surface-3'"
		:style="{ aspectRatio }"
		:data-flow-id="flow?._id || undefined"
		:data-layers="drawnLayers.length"
		:data-overlays="overlayLayers.filter((l) => l.visible).length"
		:data-drop-active="dropActive ? 'true' : undefined"
		@dragover="onDragOver"
		@dragleave="onDragLeave"
		@drop="onDrop"
	>
		<div
			ref="stage"
			class="absolute inset-0 outline-none"
			:style="stageStyle"
			tabindex="0"
			role="application"
			:aria-label="flow ? `Scene canvas: ${flow.name}${onAir ? ' (on air)' : ''}` : 'Scene canvas'"
			data-stage-bg
			@click="onStageClick"
			@keydown="onKeydown"
		>
			<!-- Layers, bottom → top -->
			<div
				v-for="layer in drawnLayers"
				:key="layer.media.id"
				class="scene-layer absolute"
				:class="[
					canEdit ? 'cursor-move' : '',
					layer.media.id === selectedId ? 'z-selected' : '',
					!layer.visible ? 'opacity-40' : '',
				]"
				:data-hidden="!layer.visible ? 'true' : undefined"
				:style="boxStyle(layer.rect, layer.z)"
				:data-layer-id="layer.media.id"
				:data-layer-type="layer.media.type"
				:data-overlay-id="layer.overlay?._id || undefined"
				@pointerdown="onLayerPointerDown($event, layer)"
			>
				<!-- content (hidden layers are ghosted at 40 % while selected) -->
				<template v-if="layer.visible || layer.media.id === selectedId">
					<img
						v-if="layer.media.type === 'image' && layer.media.imageUrl"
						:src="layer.media.imageUrl"
						:alt="layer.media.name"
						class="pointer-events-none h-full w-full object-contain"
						draggable="false"
						@load="onImageLoad($event, layer.media.id)"
					>
					<div v-else-if="layer.media.type === 'image'" class="layer-placeholder">
						<icon-base width="22" height="22" :width-view-box="24" :height-view-box="24" fill="none"><icon-image /></icon-base>
						<span>{{ layer.media.name }}</span>
					</div>

					<template v-else-if="layer.media.type === 'vod'">
						<video
							v-if="!lightweight && vodSourceUrl(layer.media)"
							:src="vodSourceUrl(layer.media)"
							class="pointer-events-none h-full w-full object-contain"
							autoplay
							loop
							playsinline
							preload="metadata"
							:muted="mutedFor(layer.media)"
							:volume.prop="volumeOf(layer.media)"
							@loadedmetadata="onVideoMeta($event, layer.media.id)"
						/>
						<div v-else class="layer-placeholder">
							<icon-base width="22" height="22"><icon-film /></icon-base>
							<span>{{ layer.media.name }}</span>
							<small>{{ lightweight ? 'Lightweight' : 'No file' }}</small>
						</div>
					</template>

					<MediaflowLiveLayer
						v-else-if="layer.media.type === 'live'"
						class="pointer-events-none"
						:stream="streamFor(layer.media)"
						:label="layer.media.name"
						:muted="mutedFor(layer.media)"
						:volume="volumeOf(layer.media)"
						:lightweight="lightweight"
						:active="layer.visible"
						@aspect="setAspect(layer.media.id, $event)"
					/>

					<template v-else-if="layer.media.type === 'url'">
						<div v-if="!lightweight && layer.media.sourceUrl" class="pointer-events-none relative h-full w-full overflow-hidden">
							<iframe
								:src="layer.media.sourceUrl"
								:title="layer.media.name"
								class="pointer-events-none absolute left-0 top-0 border-0 bg-transparent"
								:style="urlFrameStyle(layer.rect)"
								tabindex="-1"
								loading="lazy"
							/>
						</div>
						<div v-else class="layer-placeholder">
							<icon-base width="22" height="22"><icon-globe /></icon-base>
							<span>{{ layer.media.name }}</span>
							<small>{{ lightweight ? 'Lightweight' : 'No URL' }}</small>
						</div>
					</template>
				</template>

			</div>

			<!-- selection: outside the layer so handles never scale or get clipped -->
			<div
				v-if="canEdit && selectedLayer"
				ref="selectionEl"
				class="scene-selection pointer-events-none absolute"
				v-bind="selectionAttrs(selectedLayer.rect)"
				data-testid="canvas-selection"
			>
				<div class="scene-selection-frame absolute inset-0" />
				<span class="scene-selection-label absolute flex items-center gap-1 whitespace-nowrap" data-testid="canvas-selection-label">
					<span class="min-w-0 truncate rounded bg-main px-1.5 py-0.5 text-xs font-semibold text-white">{{ selectedLayer.overlay ? selectedLayer.overlay.name : selectedLayer.media.name }}</span>
					<span v-if="selectedLayer.overlay?.pendingLive" class="shrink-0 rounded bg-gold px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-darker">{{ selectedLayer.overlay.live ? 'Graphic · on after Apply' : 'Graphic · off after Apply' }}</span>
					<span v-else-if="selectedLayer.overlay" class="shrink-0 rounded px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider" :class="selectedLayer.overlay.live ? 'bg-error text-white' : 'bg-surface-3 text-surface-8'">{{ selectedLayer.overlay.live ? 'Graphic · on air' : 'Graphic · off air' }}</span>
					<span v-else-if="!selectedLayer.visible" class="shrink-0 rounded bg-surface-3 px-1.5 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wider text-surface-8">Hidden</span>
				</span>
				<span
					v-for="h in (selectedLayer.aspect ? CORNERS : HANDLES)"
					:key="h"
					class="scene-handle pointer-events-auto absolute size-2.5 rounded-sm bg-white ring-1 ring-main"
					:class="`scene-handle-${h}`"
					:style="{ cursor: handleCursor(h) }"
					:data-handle="h"
					@pointerdown="onHandlePointerDown($event, h)"
				/>
			</div>

			<!-- snap guides (positioned by showGuides during a drag) -->
			<div ref="guideX" hidden class="pointer-events-none absolute inset-y-0 left-0 w-px bg-main" style="z-index: 1000" data-guide="x" />
			<div ref="guideY" hidden class="pointer-events-none absolute inset-x-0 top-0 h-px bg-main" style="z-index: 1000" data-guide="y" />

			<!-- empty flow -->
			<div v-if="flow && !flowDrawn" class="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center">
				<p class="m-0 max-w-[260px] text-tiny text-surface-8">
					<template v-if="media.length">All sources in this scene are hidden.</template>
					<template v-else>This scene is empty.</template>
				</p>
			</div>
			<div v-else-if="!flow" class="pointer-events-none absolute inset-0 flex items-center justify-center p-6 text-center">
				<p class="m-0 text-tiny text-surface-8">{{ placeholder }}</p>
			</div>
		</div>

		<!-- Drop indicator while an input is dragged over -->
		<div v-if="dropActive" class="pointer-events-none absolute inset-0 z-[1003] flex items-center justify-center bg-main-transparent ring-2 ring-inset ring-main" data-testid="canvas-drop-hint">
			<span class="rounded-md bg-main px-3 py-1.5 text-xs font-semibold text-white shadow">Drop to add to this scene</span>
		</div>

		<!-- Loading skeleton -->
		<div v-if="loading" class="absolute inset-0 z-[1002] animate-pulse bg-surface-2" aria-busy="true" aria-label="Loading flow" />
	</div>
</template>

<style scoped>
.scene-layer {
	touch-action: none;
	transform-origin: 0 0;
}
.scene-selection {
	z-index: 900;
}
.scene-selection-label {
	top: -24px;
	left: 0;
	max-width: var(--label-max, 100%);
}
.scene-selection[data-label-right] .scene-selection-label {
	left: auto;
	right: 0;
}
.scene-selection[data-label-below] .scene-selection-label {
	top: calc(100% + 4px);
}
/* Drawn around the content, never over its pixels; inside only where flush with the canvas edge. */
.scene-selection-frame {
	--sel-l: 0 0 0 0 transparent;
	--sel-r: 0 0 0 0 transparent;
	--sel-t: 0 0 0 0 transparent;
	--sel-b: 0 0 0 0 transparent;
	box-shadow: 0 0 0 2px var(--c-main), var(--sel-l), var(--sel-r), var(--sel-t), var(--sel-b);
}
.scene-selection[data-flush-l] .scene-selection-frame { --sel-l: inset 2px 0 0 0 var(--c-main); }
.scene-selection[data-flush-r] .scene-selection-frame { --sel-r: inset -2px 0 0 0 var(--c-main); }
.scene-selection[data-flush-t] .scene-selection-frame { --sel-t: inset 0 2px 0 0 var(--c-main); }
.scene-selection[data-flush-b] .scene-selection-frame { --sel-b: inset 0 -2px 0 0 var(--c-main); }
.scene-selection[data-label-inside] .scene-selection-label {
	top: 4px;
	left: 18px;
}
.scene-selection[data-label-inside][data-label-right] .scene-selection-label {
	left: auto;
	right: 18px;
}
/* Centred on the edge (−5px), or inside it when flush with the canvas edge. */
.scene-handle-nw, .scene-handle-n, .scene-handle-ne { top: -5px; }
.scene-handle-sw, .scene-handle-s, .scene-handle-se { bottom: -5px; }
.scene-handle-nw, .scene-handle-w, .scene-handle-sw { left: -5px; }
.scene-handle-ne, .scene-handle-e, .scene-handle-se { right: -5px; }
.scene-handle-n, .scene-handle-s { left: 50%; margin-left: -5px; }
.scene-handle-e, .scene-handle-w { top: 50%; margin-top: -5px; }
.scene-selection[data-flush-t] :is(.scene-handle-nw, .scene-handle-n, .scene-handle-ne) { top: 4px; }
.scene-selection[data-flush-b] :is(.scene-handle-sw, .scene-handle-s, .scene-handle-se) { bottom: 4px; }
.scene-selection[data-flush-l] :is(.scene-handle-nw, .scene-handle-w, .scene-handle-sw) { left: 4px; }
.scene-selection[data-flush-r] :is(.scene-handle-ne, .scene-handle-e, .scene-handle-se) { right: 4px; }
.z-selected {
	/* keep handles clickable above neighbours without changing paint order of content */
	outline: none;
}
.scene-handle {
	z-index: 20;
}
.layer-placeholder {
	display: flex;
	height: 100%;
	width: 100%;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 4px;
	padding: 6px;
	text-align: center;
	background: var(--surface-2);
	color: var(--surface-text-1);
	font-size: 12px;
	font-weight: 500;
	pointer-events: none;
	overflow: hidden;
	background-image: repeating-linear-gradient(135deg, transparent 0 10px, rgba(255, 255, 255, 0.03) 10px 20px);
}
.layer-placeholder small {
	font-size: 12px;
	color: var(--surface-8);
}
.scene-canvas:fullscreen {
	border-radius: 0;
	width: 100vw;
	height: 100vh;
	aspect-ratio: auto;
	background: #000;
}
.scene-canvas:fullscreen > div[data-stage-bg] {
	inset: auto;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
}
</style>
